import React, { useState, useRef } from 'react';
import { ProcessedMds, ImdsStandard, MdsItem, Status, Millsheet } from '../types';
import * as XLSX from 'xlsx';
import { analyzeFileContent, cleanJsonString } from '../services/geminiService';
import { processMdsData } from '../utils/bomProcessor';

interface CasComplianceCheckerProps {
  savedFiles: ProcessedMds[];
  standards: ImdsStandard[];
  onViewDrawing: (dwgNumber: string, partNumber?: string) => void;
  onSaveFile?: (file: ProcessedMds) => Promise<void>;
  onDeleteFile?: (dwgNumber: string) => Promise<void>;
}

const CasComplianceChecker: React.FC<CasComplianceCheckerProps> = ({ 
  savedFiles, 
  standards, 
  onViewDrawing, 
  onSaveFile,
  onDeleteFile 
}) => {
  const [casInput, setCasInput] = useState('');
  const [drawingOption, setDrawingOption] = useState<'all' | 'selected'>('all');
  const [selectedDrawings, setSelectedDrawings] = useState<string[]>([]);
  const [regulationOption, setRegulationOption] = useState<'all' | 'selected'>('all');
  const [selectedRegulations, setSelectedRegulations] = useState<string[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'checker' | 'library'>('checker');
  const [selectedRohsFile, setSelectedRohsFile] = useState<ProcessedMds | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingSubstance, setEditingSubstance] = useState<{cas: string, value: string} | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingFiles(Array.from(files));
  };

  const processPendingFiles = async () => {
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      setUploadProgress(`Processing ${i + 1} of ${pendingFiles.length}: ${file.name}`);
      
      try {
        const reader = new FileReader();
        const dataToSend = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const result = await analyzeFileContent(
          { mimeType: file.type, data: dataToSend },
          file.name
        );

        const jsonStr = cleanJsonString(result);
        const parsedResult = JSON.parse(jsonStr);
        
        const rohsComposition: { [key: string]: string } = {};
        const rohsLimits: { [key: string]: number } = {};
        const rohsUnits: { [key: string]: string } = {};

        // Handle substances from general analysis
        if (parsedResult.substances && Array.isArray(parsedResult.substances)) {
          parsedResult.substances.forEach((sub: any) => {
            const key = sub.cas ? sub.cas.replace(/[^\d]/g, '') : sub.name.toLowerCase().replace(/\s+/g, '_');
            const ppm = (parseFloat(sub.percentage) || 0) * 10000;
            rohsComposition[key] = ppm.toString();
          });
        }

        // Handle specific rohs_data if present
        if (parsedResult.rohs_data && Array.isArray(parsedResult.rohs_data)) {
          parsedResult.rohs_data.forEach((item: any) => {
            // Use substance name as key for better display in ROHS library
            const key = item.substance || item.cas || 'Unknown';
            rohsComposition[key] = item.value || '0';
            if (item.limit) rohsLimits[key] = parseFloat(item.limit) || 0;
            if (item.unit) rohsUnits[key] = item.unit;
          });
        }

        const newItem: MdsItem = {
          no: 1,
          level: 0,
          part_number_customer: parsedResult.metadata?.part_number || file.name,
          part_number_idbm: '-',
          part_name_customer: parsedResult.metadata?.description || parsedResult.metadata?.title || 'RoHS Document',
          part_name_idbm: '-',
          qty_r: 1,
          qty_l: 0,
          qty_total: 1,
          status: Status.NEW,
          uom: 'ROHS_DOC',
          berat_level: parseFloat(parsedResult.metadata?.weight_total) || 0,
          berat_total: parseFloat(parsedResult.metadata?.weight_total) || 0,
          material: '-',
          millsheet: Millsheet.X,
          manufacturer: parsedResult.metadata?.supplier || '-',
          parent_part_number: '',
          imds_number: parsedResult.metadata?.imds_id || '-',
          details: {
            pdfs: [],
            customFields: [
              { id: 'summary', label: 'AI Summary', value: parsedResult.summary || '' },
              { id: 'report_date', label: 'Report Date', value: parsedResult.metadata?.report_date || '' }
            ],
            rohsComposition,
            rohsLimits,
            rohsUnits
          }
        };

        const { data } = processMdsData({
            customer: parsedResult.metadata?.recipient || 'RoHS Analysis',
            dwg_number: parsedResult.metadata?.part_number || file.name.replace(/\.[^/.]+$/, ""),
            hr_mark: '-',
            status: Status.NEW
        }, [newItem]);

        if (data && onSaveFile) {
             const newFile: ProcessedMds = { ...data, uploadedAt: new Date().toISOString() };
             await onSaveFile(newFile);
             successCount++;
        }
        
        // Add a small delay between files to avoid rate limits
        if (i < pendingFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      } catch (err: any) {
        console.error(`Failed to process ${file.name}:`, err);
        setUploadProgress(prev => `${prev}\nError: ${err.message || 'Unknown error'}`);
        // Still wait a bit before next file even if error
        if (i < pendingFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
    }

    setIsUploading(false);
    setUploadProgress('');
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    alert(`Successfully processed and saved ${successCount} out of ${pendingFiles.length} files.`);
  };

  const handleDeleteRohs = async (dwgNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDeleteFile) return;
    await onDeleteFile(dwgNumber);
    if (selectedRohsFile?.dwg.dwg_number === dwgNumber) {
      setSelectedRohsFile(null);
    }
  };

  const handleUpdateRohsValue = async () => {
    if (!selectedRohsFile || !editingSubstance || !onSaveFile) return;

    const updatedFile = { ...selectedRohsFile };
    if (updatedFile.items[0]?.details) {
      updatedFile.items[0].details.rohsComposition = {
        ...updatedFile.items[0].details.rohsComposition,
        [editingSubstance.cas]: editingSubstance.value
      };
      await onSaveFile(updatedFile);
      setSelectedRohsFile(updatedFile);
      setEditingSubstance(null);
    }
  };

  const handleCheck = async () => {
    if (!casInput.trim()) {
      alert('Please enter at least one CAS number.');
      return;
    }

    setLoading(true);
    try {
      const casNumbers = casInput.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
      
      const drawingsToSearch = drawingOption === 'all' 
        ? savedFiles 
        : savedFiles.filter(f => selectedDrawings.includes(f.dwg.dwg_number));

      const standardsToSearch = regulationOption === 'all'
        ? standards
        : standards.filter(std => selectedRegulations.includes(std.id));

      if (standardsToSearch.length === 0) {
        alert('Warning: No regulations are selected or available. All CAS numbers will be marked as Safe. Please upload and save a regulation in the Compliance menu first.');
      }

      const newResults = casNumbers.map(cas => {
        const cleanCas = cas.replace(/[^a-zA-Z0-9]/g, '');
        const affected: { dwg: string, part: string }[] = [];

        drawingsToSearch.forEach(file => {
          file.items.forEach(item => {
            if (item.details?.rohsComposition) {
              const hasCas = Object.keys(item.details.rohsComposition).some(k => k.replace(/[^a-zA-Z0-9]/g, '') === cleanCas);
              if (hasCas) {
                affected.push({ dwg: file.dwg.dwg_number, part: item.part_number_customer });
              }
            }
          });
        });

        let status = 'Safe';
        let reason = 'No restrictions found.';
        let isBanned = false;
        let isRestricted = false;
        let reasons: string[] = [];

        standardsToSearch.forEach(std => {
          std.regulatoryRows?.forEach(row => {
            const rowCas = (row.casNumber || '').replace(/[^a-zA-Z0-9]/g, '');
            if (rowCas && rowCas === cleanCas) {
              // Combine all text columns to search for criteria keywords
              // because different Excel formats might put the status in different columns
              const fullRowText = [
                row.hondaCriteria,
                row.imdsAppId,
                row.hondaApplication,
                row.mainApplications,
                row.relevantLaws,
                row.substance
              ].join(' ').toLowerCase();

              if (fullRowText.includes('prohibited') || fullRowText.includes('banned')) {
                isBanned = true;
                reasons.push(`Prohibited by ${std.standardName}`);
              } else if (fullRowText.includes('declarable') || fullRowText.includes('restricted')) {
                isRestricted = true;
                reasons.push(`Declarable/Restricted by ${std.standardName}`);
              } else {
                const criteriaText = row.hondaCriteria && row.hondaCriteria !== '-' ? row.hondaCriteria : 'No specific criteria found';
                reasons.push(`Listed in ${std.standardName} (${criteriaText})`);
              }
            }
          });
        });

        if (isBanned) {
          status = 'Banned';
          reason = Array.from(new Set(reasons)).join('; ');
        } else if (isRestricted) {
          status = 'Restricted';
          reason = Array.from(new Set(reasons)).join('; ');
        } else if (reasons.length > 0) {
          status = 'Restricted'; // Fallback if it's listed but criteria is ambiguous
          reason = Array.from(new Set(reasons)).join('; ');
        }

        return {
          casNumber: cas,
          status,
          reason,
          affectedDrawings: affected
        };
      });

      await new Promise(resolve => setTimeout(resolve, 800));
      
      setResults(newResults);
    } catch (err) {
      console.error(err);
      alert('Error checking CAS numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (results.length === 0) return;
    
    const exportData = results.flatMap(r => {
      if (r.affectedDrawings.length === 0) {
        return [{
          'CAS Number': r.casNumber,
          'Status': r.status,
          'Reason': r.reason,
          'Affected Drawing': '-',
          'Affected Part': '-'
        }];
      }
      return r.affectedDrawings.map((aff: any) => ({
        'CAS Number': r.casNumber,
        'Status': r.status,
        'Reason': r.reason,
        'Affected Drawing': aff.dwg,
        'Affected Part': aff.part
      }));
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CAS Compliance");
    XLSX.writeFile(wb, `CAS_Compliance_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Banned':
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">BANNED</span>;
      case 'Restricted':
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">RESTRICTED</span>;
      case 'Safe':
      default:
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">SAFE</span>;
    }
  };

  const handleExportLibrary = () => {
    if (rohsFiles.length === 0) return;
    
    // Get all unique substances to use as column headers
    const allSubstances = new Set<string>();
    rohsFiles.forEach(file => {
      const item = file.items[0];
      if (item?.details?.rohsComposition) {
        Object.keys(item.details.rohsComposition).forEach(cas => allSubstances.add(cas));
      }
    });

    const substanceList = Array.from(allSubstances).sort();

    const exportData = rohsFiles.map(file => {
      const item = file.items[0];
      const row: any = {
        'Document No': file.dwg.dwg_number,
        'Customer/Supplier': file.dwg.customer,
        'Uploaded At': new Date(file.uploadedAt).toLocaleString(),
      };
      
      // Add columns for each substance
      substanceList.forEach(substance => {
        row[substance] = item?.details?.rohsComposition?.[substance] || '0';
      });
      
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ROHS Library");
    XLSX.writeFile(wb, `ROHS_Data_Library_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportCurrentRohs = () => {
    if (!selectedRohsFile) return;
    
    const item = selectedRohsFile.items[0];
    if (!item?.details?.rohsComposition) return;

    const exportData = Object.entries(item.details.rohsComposition).map(([cas, val]) => ({
      'Substance / CAS': cas,
      'Value': val,
      'Limit': item.details?.rohsLimits?.[cas] || '-',
      'Unit': item.details?.rohsUnits?.[cas] || 'PPM',
      'Status': (item.details?.rohsLimits?.[cas] !== undefined) 
        ? (parseFloat(val || '0') > (item.details.rohsLimits[cas] || 0) ? 'FAIL' : 'PASS')
        : 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ROHS Document");
    XLSX.writeFile(wb, `ROHS_Report_${selectedRohsFile.dwg.dwg_number}.xlsx`);
  };

  const rohsFiles = savedFiles.filter(f => f.items.some(i => i.uom === 'ROHS_DOC' || (i.details?.rohsComposition && Object.keys(i.details.rohsComposition).length > 0)));

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="border-b border-slate-200 dark:border-slate-700 pb-6 flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">CAS Compliance Checker</h2>
          <p className="text-sm text-slate-500 mt-1">Verify substance compliance across your drawings</p>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
          <button 
            onClick={() => setActiveSubTab('checker')}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${activeSubTab === 'checker' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
          >
            Compliance Checker
          </button>
          <button 
            onClick={() => setActiveSubTab('library')}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${activeSubTab === 'library' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
          >
            ROHS Data Library
          </button>
        </div>
      </div>

      {activeSubTab === 'checker' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Section */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-xl shadow-sm space-y-4">
              <div className="space-y-2 pb-4 border-b border-slate-100 dark:border-slate-700">
                <label className="text-xs font-medium text-slate-500 uppercase flex justify-between items-center">
                  <span>Upload RoHS PDFs</span>
                  {isUploading && <span className="text-blue-500 animate-pulse text-[10px]">{uploadProgress}</span>}
                </label>
                <input 
                  type="file" 
                  multiple 
                  accept=".pdf"
                  ref={fileInputRef}
                  onChange={handleFileSelection}
                  disabled={isUploading}
                  className="block w-full text-sm text-slate-500 dark:text-slate-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    dark:file:bg-blue-900/30 dark:file:text-blue-400
                    hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50
                    cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {pendingFiles.length > 0 && !isUploading && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 animate-in slide-in-from-top-2">
                    <div className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase mb-2">Files to Process ({pendingFiles.length})</div>
                    <ul className="space-y-1 mb-3">
                      {pendingFiles.map((f, i) => (
                        <li key={i} className="text-[10px] text-slate-600 dark:text-slate-400 truncate flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-blue-400"></div>
                          {f.name}
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={processPendingFiles}
                      className="w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      Extract & Save Data
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-slate-400">Upload multiple RoHS PDF files to extract and save data.</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-500 uppercase">CAS Numbers (Single or Bulk)</label>
                <textarea 
                  className="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-sm font-mono text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none" 
                  placeholder="e.g. 7439-92-1&#10;1333-86-4&#10;7429-90-5"
                  value={casInput}
                  onChange={e => setCasInput(e.target.value)}
                />
                <p className="text-[10px] text-slate-400">Separate multiple CAS numbers with commas or new lines.</p>
              </div>

              <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                <label className="text-xs font-medium text-slate-500 uppercase">Target Drawings</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input 
                      type="radio" 
                      name="drawingOption" 
                      value="all" 
                      checked={drawingOption === 'all'} 
                      onChange={() => setDrawingOption('all')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    All Drawings
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input 
                      type="radio" 
                      name="drawingOption" 
                      value="selected" 
                      checked={drawingOption === 'selected'} 
                      onChange={() => setDrawingOption('selected')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    Selected
                  </label>
                </div>
              </div>

              {drawingOption === 'selected' && (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700">
                  {savedFiles.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No drawings available in Data Archives.</p>
                  ) : (
                    savedFiles.map(file => (
                      <label key={file.dwg.dwg_number} className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 p-1.5 rounded transition-colors">
                        <input 
                          type="checkbox" 
                          checked={selectedDrawings.includes(file.dwg.dwg_number)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDrawings([...selectedDrawings, file.dwg.dwg_number]);
                            } else {
                              setSelectedDrawings(selectedDrawings.filter(id => id !== file.dwg.dwg_number));
                            }
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex flex-col">
                          <span className="font-mono font-medium">{file.dwg.dwg_number}</span>
                          <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{file.dwg.customer}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}

              <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                <label className="text-xs font-medium text-slate-500 uppercase">Target Regulations</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input 
                      type="radio" 
                      name="regulationOption" 
                      value="all" 
                      checked={regulationOption === 'all'} 
                      onChange={() => setRegulationOption('all')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    All Regulations
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input 
                      type="radio" 
                      name="regulationOption" 
                      value="selected" 
                      checked={regulationOption === 'selected'} 
                      onChange={() => setRegulationOption('selected')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    Selected
                  </label>
                </div>
              </div>

              {regulationOption === 'selected' && (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700">
                  {standards.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No regulations available in Compliance menu.</p>
                  ) : (
                    standards.map(std => (
                      <label key={std.id} className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 p-1.5 rounded transition-colors">
                        <input 
                          type="checkbox" 
                          checked={selectedRegulations.includes(std.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRegulations([...selectedRegulations, std.id]);
                            } else {
                              setSelectedRegulations(selectedRegulations.filter(id => id !== std.id));
                            }
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex flex-col">
                          <span className="font-medium">{std.standardName}</span>
                          <span className="text-[10px] text-slate-500">{std.ownerCompany}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}

              <button 
                onClick={handleCheck} 
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Checking...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Run Compliance Check
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-between items-center">
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide">Analysis Results</h3>
                {results.length > 0 && (
                  <button 
                    onClick={handleExport}
                    className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    Export to Excel
                  </button>
                )}
              </div>
              
              <div className="flex-1 overflow-auto">
                {results.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center p-12 text-slate-400">
                    <svg className="w-12 h-12 mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                    <p>Enter CAS numbers and run check to see results.</p>
                  </div>
                ) : (
                  <table className="w-full text-left text-sm border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase text-xs sticky top-0">
                      <tr>
                        <th className="p-4">CAS Number</th>
                        <th className="p-4 text-center">Status</th>
                        <th className="p-4">Reason / Details</th>
                        <th className="p-4">Affected Drawings & Materials</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {results.map((res, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="p-4 font-mono text-slate-900 dark:text-white">{res.casNumber}</td>
                          <td className="p-4 text-center">{getStatusBadge(res.status)}</td>
                          <td className="p-4 text-slate-600 dark:text-slate-400 text-xs">{res.reason}</td>
                          <td className="p-4">
                            {res.affectedDrawings && res.affectedDrawings.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {res.affectedDrawings.map((aff: any, i: number) => (
                                  <button 
                                    key={i} 
                                    onClick={() => onViewDrawing(aff.dwg, aff.part)}
                                    className="text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-xs transition-colors cursor-pointer border border-blue-200 dark:border-blue-800 flex flex-col group"
                                  >
                                    <span className="font-bold group-hover:underline">{aff.dwg}</span>
                                    <span className="text-[10px] opacity-80 font-mono mt-0.5">Part: {aff.part}</span>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs italic">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* ROHS Library Sidebar */}
          <div className="lg:col-span-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden flex flex-col max-h-[600px]">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-between items-center">
              <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide">ROHS Document List</h3>
              {rohsFiles.length > 0 && (
                <button 
                  onClick={handleExportLibrary}
                  className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                  title="Export All Library"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
              {rohsFiles.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm italic">No ROHS data found. Upload ROHS PDFs in the checker tab.</div>
              ) : (
                rohsFiles.map(file => (
                  <div 
                    key={file.dwg.dwg_number}
                    onClick={() => setSelectedRohsFile(file)}
                    className={`w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer relative group ${selectedRohsFile?.dwg.dwg_number === file.dwg.dwg_number ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600' : ''}`}
                  >
                    <div className="font-bold text-slate-900 dark:text-white text-sm">{file.dwg.dwg_number}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{file.dwg.customer}</div>
                    <div className="text-[10px] text-slate-400 mt-2 flex justify-between items-center">
                      <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                      <span>{Object.keys(file.items[0]?.details?.rohsComposition || {}).length} Substances</span>
                    </div>
                    <button 
                      onClick={(e) => handleDeleteRohs(file.dwg.dwg_number, e)}
                      className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ROHS Details Table */}
          <div className="lg:col-span-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden flex flex-col min-h-[400px]">
            {selectedRohsFile ? (
              <>
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide">Extracted Substances: {selectedRohsFile.dwg.dwg_number}</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5">{selectedRohsFile.dwg.customer}</p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleExportCurrentRohs}
                      className="px-3 py-1 bg-green-600 text-white text-[10px] font-medium rounded hover:bg-green-700 transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Export PDF Data
                    </button>
                    {selectedRohsFile.items[0]?.details?.customFields?.find(f => f.id === 'report_date')?.value && (
                      <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-[10px] font-medium text-slate-600 dark:text-slate-400">
                        Report Date: {selectedRohsFile.items[0].details.customFields.find(f => f.id === 'report_date')?.value}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase text-xs sticky top-0">
                      <tr>
                        <th className="p-4">Substance / CAS</th>
                        <th className="p-4 text-right">Value</th>
                        <th className="p-4 text-right">Limit</th>
                        <th className="p-4 text-center">Unit</th>
                        <th className="p-4 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {Object.entries(selectedRohsFile.items[0]?.details?.rohsComposition || {}).map(([cas, val]) => {
                        const limit = selectedRohsFile.items[0]?.details?.rohsLimits?.[cas];
                        const unit = selectedRohsFile.items[0]?.details?.rohsUnits?.[cas] || 'PPM';
                        const numericVal = parseFloat(val || '0');
                        const isExceed = limit !== undefined && numericVal > limit;

                        return (
                          <tr key={cas} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group">
                            <td className="p-4">
                              <div className="font-medium text-slate-900 dark:text-white">{cas}</div>
                              <div className="text-[10px] text-slate-400 uppercase">Substance Name / ID</div>
                            </td>
                            <td className="p-4 text-right font-mono text-slate-900 dark:text-white">
                              {editingSubstance?.cas === cas ? (
                                <div className="flex items-center justify-end gap-2">
                                  <input 
                                    type="text" 
                                    value={editingSubstance.value}
                                    onChange={(e) => setEditingSubstance(prev => prev ? {...prev, value: e.target.value} : null)}
                                    className="w-20 px-2 py-1 bg-white dark:bg-slate-900 border border-blue-500 rounded text-right outline-none"
                                    autoFocus
                                  />
                                  <button onClick={handleUpdateRohsValue} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                  </button>
                                  <button onClick={() => setEditingSubstance(null)} className="p-1 text-slate-400 hover:bg-slate-50 rounded">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-2">
                                  <span>{val}</span>
                                  <button 
                                    onClick={() => setEditingSubstance({cas, value: val || ''})}
                                    className="p-1 text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all rounded"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="p-4 text-right font-mono text-slate-500">{limit ?? '-'}</td>
                            <td className="p-4 text-center text-slate-500 text-xs">{unit}</td>
                            <td className="p-4 text-center">
                              {limit !== undefined ? (
                                isExceed ? (
                                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-bold">FAIL</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-bold">PASS</span>
                                )
                              ) : (
                                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400 text-[10px] font-bold">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {selectedRohsFile.items[0]?.details?.customFields?.find(f => f.id === 'summary')?.value && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-1">AI Summary</h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed italic">
                      {selectedRohsFile.items[0].details.customFields.find(f => f.id === 'summary')?.value}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-slate-400">
                <svg className="w-16 h-16 mb-4 opacity-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <p className="text-sm">Select a document from the library to view extracted data.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CasComplianceChecker;
